import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Share2, Heart, Minus, Plus } from 'lucide-react';
import '../components/styles/ProductDetail.css';
import CustomSelect from "../components/CustomSelect";
import {FaRegFrown} from "react-icons/fa";
import {useGetProductMutation} from "../api/ProductAPI";
import ProductDetailExtra from "../components/item/ProductDetailExtra";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useLoggedInUserId} from "../api/GetLoggedInUserId";
import LoginRequiredModal from "../components/item/LoginRequiredModal";
import CartErrorPopup from "../components/cart/CartErrorPopup";
import RemoteExperiencePopup from "../components/item/RemoteExperiencePopup";
import { toast } from "../utils/toast";

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [productData, setProductData] = useState({
        item: null,
        popularReviews: [],
        reviews: [],
        qnaArticles: []
    });
    const [loading, setLoading] = useState(true);
    const currentItemId = parseInt(id, 10);
    const [quantity, setQuantity] = useState(1);
    const [selectedSubscription, setSelectedSubscription] = useState('1');
    const [finalPrice, setFinalPrice] = useState(0);
    const [originalPrice, setOriginalPrice] = useState(0);
    const [salePrice, setSalePrice] = useState(0);
    const [discountRate, setDiscountRate] = useState(0);

    const [products, setProducts] = React.useState([]);
    const [error, setError] = React.useState(null);
    const productMuation = useGetProductMutation();
    // ✅ 모달 상태 추가
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [cartPopup, setCartPopup] = useState({ isOpen: false, message: "" });

    const [isRemotePopupOpen, setIsRemotePopupOpen] = useState(false);

    // 페이지 로딩 시 상품 데이터를 받아옴
    React.useEffect(() => {
        const getProductList = async () => {
            const response = await fetch("/api/items", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            const data = await response.json();
            if (response.ok) {
                setProducts(data.items);
                setLoading(false);
            } else {
                const errorData = await response.json();
                setError(errorData.message || "상품 데이터 불러오기에 실패했습니다.");
                setLoading(false);
                console.log(errorData.message || "상품 데이터 불러오기에 실패했습니다.");
            }
        };

        getProductList();
    }, []);

    const subscriptionDiscounts = {
        'original': 0,
        '1': 0,
        '3': 3,
        '6': 5,
        '12': 10,
        // '무제한': 15
    };

    const calculateOriginalPrice = (basePrice, months) => {
        if (months === "무제한"){
            return Math.round(basePrice * 20);
        }
        return Math.round(basePrice * months);
    };

    const calculateSalePrice = (basePrice, months) => {
        const discount = subscriptionDiscounts[String(months)];
        if (discount === 15) {
            return Math.round(basePrice * 20 * (1 - discount / 100));
        } else {
            return Math.round(basePrice * months * (1 - discount / 100));
        }
    };

    const calculateFinalPrice = (salePrice, quantity, selectedMonths) => {
        switch (selectedMonths) {
            case "1":
                return salePrice * quantity;
            case "3":
                return salePrice * quantity * selectedMonths * 0.97;
            case "6":
                return salePrice * quantity * selectedMonths * 0.95;
            case "12":
                return salePrice * quantity * selectedMonths * 0.9;
            case "무제한":
                return salePrice * quantity * 24 * 0.85;
        }

    };

    useEffect(() => {
        console.log("로그인된 사용자 ID: ", memberId);
        const fetchProduct = async () => {
            try {
                const response = await fetch(`/api/items/detail?itemId=${id}`);
                const data = await response.json();
                setProductData({
                    item: data.item,
                    popularReviews: data.popularReviews === "noData" ? [] : data.popularReviews,
                    reviews: data.reviews === "noData" ? [] : data.reviews,
                    qnaArticles: data.qnaArticles === "noData" ? [] : data.qnaArticles
                });

                setLoading(false);
            } catch (error) {
                console.error('상품 정보를 불러오는데 실패했습니다:', error);
                setLoading(false);
            }
        };

        fetchProduct();
    }, [id]);

    useEffect(() => {
        if (!productData.item) return;

        const basePrice = productData.item.salePrice;
        const months = selectedSubscription === "무제한" ? "무제한" : parseInt(selectedSubscription, 10);

        const newOriginalPrice = calculateOriginalPrice(basePrice, selectedSubscription);
        const newSalePrice = calculateSalePrice(basePrice, selectedSubscription);
        const newFinalPrice = calculateFinalPrice(basePrice, quantity, selectedSubscription);

        console.log((newFinalPrice).toLocaleString());

        setOriginalPrice(newOriginalPrice);
        setSalePrice(newSalePrice);
        setFinalPrice(newFinalPrice);
        setDiscountRate(subscriptionDiscounts[selectedSubscription]);
    }, [productData.item, selectedSubscription, quantity]);

    const handleSubscriptionChange = (value) => {
        setSelectedSubscription(value);
        setQuantity(1);
    };

    const handleQuantityChange = (type) => {
        setQuantity((prevQuantity) => {
            if (type === 'decrease' && prevQuantity > 1) return prevQuantity - 1;
            if (type === 'increase') return prevQuantity + 1;
            return prevQuantity;
        });
    };

    const getProductDetail = (itemId) => {
        navigate(`/products/${itemId}`);

        // 스크롤 맨 위로 이동
        window.scrollTo(0, 0);
    };

    const queryClient = useQueryClient();
    const { data: memberId } = useLoggedInUserId();

    const addToCart = () => {
        if (!memberId) {
            setIsLoginModalOpen(true);
            return;
        }

        addToCartMutation.mutate({
            itemId: productData.item.itemId,
            count: quantity,
            subscriptionPeriod: selectedSubscription,
        });
    };

    // ✅ 장바구니 추가 Mutation 수정
    const addToCartMutation = useMutation({
        mutationFn: async ({ itemId, count, subscriptionPeriod }) => {
            if (subscriptionPeriod === "무제한") subscriptionPeriod = 0;

            const response = await fetch(`/api/addwishlist?memberId=${memberId}&itemId=${itemId}&count=${count}&subscriptionPeriod=${subscriptionPeriod}`, { method: "GET" });
            const data = await response.json();

            if (data.statusCode === 201) throw new Error("장바구니에 이미 담긴 상품입니다.");
            if (data.statusCode !== 200) throw new Error("장바구니 추가 실패");

            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["cartItems"]);
            setCartPopup({ isOpen: true, message: "✅ 장바구니에 추가되었습니다!" });
        },
        onError: (error) => {
            setCartPopup({ isOpen: true, message: `❌ ${error.message}` });
        },
    });

    const formatPrice = (price) => price?.toLocaleString() || "0";

    // ✅ 주문서 생성 Mutation
    const createOrderMutation = useMutation({


        mutationFn: async () => {
            const orderData = {
                memberId,
                orderType: "NEW",
                items: [{
                    itemId: productData.item.itemId,
                    month: parseInt(selectedSubscription, 10),
                    quantity: quantity,
                }],
            };

            console.log(orderData);

            const response = await fetch(`/api/order`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(orderData),
            });

            const data = await response.json();
            if (data.result !== "available") throw new Error("주문서 생성 실패");

            console.log("주문서 ID : ", data);
            return data; // ✅ 생성된 주문서 ID 반환
        },
        onSuccess: (data) => {
            console.log("주문서 생성 완료");
            navigate(`/order/checkout`, { state: { data } });
        },
        onError: () => {
            console.error("주문서 생성 실패:", error);
            toast("주문서를 생성하는 데 실패했습니다. 다시 시도해주세요.");
        }
    });

    // ✅ 결제 진행
    const handleCheckout = () => {
        // 로그인 여부 확인
        if (!memberId) {
            setIsLoginModalOpen(true);
            return;
        }

        createOrderMutation.mutate(); // ✅ 주문서 생성 후 결제 페이지로 이동
    };

    // 원격 체험 버튼 클릭 핸들러
    const handleRemoteExperience = () => {
        // 원격 소개 및 사용법 팝업 띄우기
        setIsRemotePopupOpen(true);
    };

    const getCategory = {
        'DISCOUNTED': '할인 상품',
        'BEST' : '베스트',
        'NEW' : '신상품',
        'NCOMPANY' : 'N사',
        'KCOMPANY' : 'K사',
        'MARKETING' : '마케팅',
        'ETC' : '기타'
    }

    if (loading) {
        return <div className="loading">Loading...</div>;
    }

    return (
        <div className="product-detail-container">

            {/* 경로 표시 */}
            <div className="breadcrumb">
                프로그램 상품 {'>'} {getCategory[productData.item?.category]}
            </div>
            <div className="product-main-section">
                {/* 상품 이미지 */}
                <div className="product-image-section">
                    {/*<img*/}
                    {/*    src={productData.item?.attachFiles?.find(file => file.attachPosition === 'THUMBNAIL')?.filePath || "/images/product_placeholder.png"}*/}
                    {/*    alt="상품 이미지"*/}
                    {/*    className="main-product-image"*/}
                    {/*/>*/}
                    {productData.item && productData.item.thumbPath ? (
                        <img
                            src={productData.item.thumbPath || "/placeholder.svg"}
                            alt={productData.item.itemName}
                            className="main-product-image"
                        />
                    ) : (
                        <div className="main-product-image">
                            <span className="main-product-image">No Image</span>
                        </div>
                    )}
                    <button className="wish-button" onClick={handleRemoteExperience}>
                        원격체험 하기
                    </button>
                </div>

                {/* 원격 소개 팝업 */}
                <RemoteExperiencePopup
                    isOpen={isRemotePopupOpen}
                    onClose={() => setIsRemotePopupOpen(false)}
                />

                {/* 상품 정보 */}
                <div className="product-info-section">
                    <h1 className="product-detail-title">
                        {productData.item?.itemName}
                    </h1>
                    <p className="product-subtitle">
                        {productData.item?.itemDescription}
                    </p>

                    <div className="price-info">
                        <span className="cell-price-label">할인율</span>
                        <div className="discount-rate">
                            {productData.item?.discountRate > 0 ? `${productData.item?.discountRate + discountRate }%` : '0%'}
                        </div>

                        <div className="price-details">
                            <span className="original-price">
                                {formatPrice(productData.item?.costPrice)}원
                            </span>
                            <span className="sale-price">
                                {formatPrice(productData.item?.salePrice)}원
                            </span>
                        </div>
                    </div>

                    {/* 상품 정보 테이블 */}
                    <table className="product-details-table">
                        <tbody>
                        <tr>
                            <th>설치방법</th>
                            <td>{productData.item?.installMethod}</td>
                        </tr>
                        <tr>
                            <th>사용환경</th>
                            <td>{productData.item?.environment}</td>
                        </tr>
                        <tr>
                            <th>A/S지원</th>
                            <td>{productData.item?.asSupport}</td>
                        </tr>
                        </tbody>
                    </table>

                    {/* 수량 선택 */}
                    <div className="quantity-section">
                        {/*<div className="quantity-control">*/}
                        {/*    <div className="quantity-label">구독개월 선택</div>*/}
                        {/*    <div className="quantity-selector">*/}
                        {/*        <select defaultValue="1">*/}
                        {/*            <option value="default">---------------</option>*/}
                        {/*            <option value="1">1 개월</option>*/}
                        {/*            <option value="3">3 개월</option>*/}
                        {/*            <option value="6">6 개월</option>*/}
                        {/*            <option value="6">12 개월</option>*/}
                        {/*            <option value="6">무제한 이용</option>*/}
                        {/*        </select>*/}
                        {/*    </div>*/}
                        {/*</div>*/}
                        <CustomSelect
                            onChange={handleSubscriptionChange}
                            value={selectedSubscription}
                        ></CustomSelect>
                        <div className="quantity-control">
                            <span className="quantity-label">수량 선택</span>
                            <div className="quantity-buttons">
                                <button onClick={() => handleQuantityChange('decrease')}>
                                    <Minus size={16} />
                                </button>
                                <span>{quantity}개</span>
                                <button onClick={() => handleQuantityChange('increase')}>
                                    <Plus size={16} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* 총 금액 섹션 수정 */}
                    <div className="total-price">
                        <span>총 구매 금액</span>
                        <div className="price-amount">
                            {discountRate > 0 && (<span className="installment">
                        {selectedSubscription}개월 구매 시 {discountRate}% 추가할인!
                        </span>)}
                            <span className="final-price">
                                {finalPrice?.toLocaleString()}원
                            </span>
                        </div>
                    </div>

                    {/* 구매 버튼 */}
                    <div className="purchase-buttons">
                        <button className="cart-button" onClick={addToCart}>장바구니</button>
                        <LoginRequiredModal isOpen={isLoginModalOpen}
                                            onClose={() => setIsLoginModalOpen(false)}
                                            onConfirm={() => {
                                                // 화면 최상단 이동
                                                window.scrollTo(0, 0);
                                                // 로그인 페이지로 이동
                                                navigate("/login");

                                            }} />
                        <CartErrorPopup isOpen={cartPopup.isOpen}
                                        message={cartPopup.message}
                                        onClose={() => setCartPopup({ isOpen: false, message: "" })} />
                        <button className="buy-button" onClick={handleCheckout}>결제하기</button>
                    </div>
                </div>
            </div>

            {/* 연관 상품 */}
            <div className="related-products">
                <h2>인기 상품</h2>
                {products.length === 0 ?
                    // 상품이 존재 하지 않을 경우 표시
                    <div className="no-product-container">
                        {/*<div className="no-product-title-section">*/}
                        {/*    <h2 className="no-product-title">404 NOT FOUND</h2>*/}
                        {/*</div>*/}
                        <div className="no-product-image">
                            {/*<img src="/images/icon/no_product.png" alt="no-products"/>*/}
                            {/*// react no-content 아이콘 추천해*/}
                            <FaRegFrown size={100} color="#6C95FF"/>

                        </div>
                        <div className="no-product-content">
                            <p className="no-product-description">현재 판매중인 상품이 없습니다.</p>
                        </div>
                    </div>
                    :
                    // 상품이 존재할 경우 표시
                    <div className="product-grid">
                        {products
                            .filter(product => product.itemId !== currentItemId) // 현재 상품 제외
                            .slice(0, 4) // 4개만 가져오기
                            .map((product, index) => (
                            <div key={index} className="product-card relative" role="button" onClick={() => getProductDetail(product.itemId)}>
                                {product.category === 'DISCOUNTED' && (
                                    <div className="discount-badge absolute top-3 left-3 bg-red-500 text-white px-3 py-1 rounded-full shadow-md text-sm font-semibold z-10 animate-pulse">
                                        할인상품
                                    </div>
                                )}
                                <div className="product-image">
                                    {product.thumbFileName && product.thumbPath ? (
                                        <img src={product.thumbPath || "/placeholder.svg"} alt={product.itemName} className="w-full h-full object-cover rounded-xl" />
                                    ) : (
                                        <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded-xl">
                                            <span className="text-gray-400">No Image</span>
                                        </div>
                                    )}
                                </div>
                                <h2 className="product-title mt-2 font-semibold text-lg">{product.itemName}</h2>
                                <p className="product-description text-gray-600 text-sm">{product.itemDescription}</p>
                                <p id="product-original-price" className="line-through text-gray-400">{product.costPrice?.toLocaleString()}원</p>
                                <div className="product-price-grid-main flex items-center justify-between mt-1">
                                    <p className="product-discount-rate text-red-500 font-bold">{product.discountRate}%</p>
                                    <p className="product-price text-xl font-bold text-gray-800">{product.salePrice?.toLocaleString()}원</p>
                                </div>
                            </div>
                        ))}
                    </div>
                }
            </div>

            {/* 상세 이미지 / 리뷰 / QnA 섹션 */}
            <ProductDetailExtra item={productData.item} reviews={productData.reviews} qa={productData.qnaArticles} />
        </div>
    );
};

export default ProductDetail;